import lodashGet from "lodash/get";
import { feature } from "@turf/turf";
import store from "@/store";
import { MAP_ICON_NAMES } from "@/common/constants/maps";
import { mapMarkerData, removeMapData } from "@/common/maps/features";
import { getDefaultMarkerOptions } from "@/common/maps/styles";
import {
  GET_SEARCH_KEYWORD,
  GET_SEARCH_LOCATION_REFRESH_MAP,
} from "@/store/actions.type";
import { SET_SEARCH_LOCATION } from "@/store/mutations.type";
import { MODULE_PREFIX_SEARCH } from "@/store";
import { EVENT_ACTIONS, SEARCH_CATEGORIES } from "@/common/constants/analytics";
import { pageTrackerLogger } from "@/common/pageTracker";

class SearchControl {
  _store = store;
  _container = null;
  _button = null;
  _inputContainer = null;
  _inputKeyword = null;
  _inputLocation = null;
  _icon = null;
  _map = null;
  _features = null;
  _autocomplete = null;
  _listenerKeyword = null;
  _listenerLocation = null;
  _place = null;
  _layerId = "search-place";
  _watcher = null;
  _showKeyword = null;

  constructor(options) {
    this._showKeyword =
      options && options.showKeyword ? options.showKeyword : false;

    this._container = document.createElement("div");
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group hyp-search";
    this._container.addEventListener("contextmenu", (e) => e.preventDefault());

    this._inputKeyword = document.createElement("input");
    this._inputKeyword.className = "hyp-ctrl-input-keyword";
    this._inputKeyword.id = "keyword";
    this._inputKeyword.placeholder = "Keyword";
    this._inputKeyword.type = "text";
    this._inputKeyword.onKeyDown = this.searchKeyword;
    this._inputKeyword.style.display = "inline";
    if (!this._showKeyword) {
      this._inputKeyword.style.display = "none";
    }

    this._inputKeyword.addEventListener("keyup", (event) => {
      if (event.keyCode === 13) {
        this.searchKeyword();
      }
    });

    this._inputLocation = document.createElement("input");
    this._inputLocation.className = "hyp-ctrl-input-location";
    this._inputLocation.id = "place";
    this._inputLocation.placeholder = "Location";
    this._inputLocation.type = "text";

    this._icon = document.createElement("i");
    this._icon.title = "Clear search";
    this._icon.className = "fa fa-times hyp-ctrl-close-icon";
    this._icon.addEventListener("click", () => {
      if (
        (this._inputKeyword.value && this._inputKeyword.value.length) ||
        (this._inputLocation.value && this._inputLocation.value.length)
      ) {
        this.clear();
      }
    });

    this._inputContainer = document.createElement("div");
    this._inputContainer.className = "hyp-ctrl-input-container";

    this._inputContainer.appendChild(this._inputLocation);
    this._inputContainer.appendChild(this._inputKeyword);
    this._inputContainer.appendChild(this._icon);

    this._container.appendChild(this._inputContainer);

    this._button = document.createElement("button");
    this._button.title = "Search";
    this._button.className = "mapboxgl-ctrl-icon hyp-search-button";
    this._button.addEventListener("click", () => {
      if (this._inputContainer.style.display === "inline") {
        this.close();
      } else {
        this.open();
      }
    });
    this._container.appendChild(this._button);

    let zoomControls = document.querySelectorAll(
      ".mapboxgl-ctrl.mapboxgl-ctrl-group"
    );
    if (zoomControls) {
      zoomControls.forEach((ele) => {
        ele.style.position = "relative";
        ele.style.top = "55px";
        ele.style.marginLeft = "10px";
      });
    }
  }

  onAdd(map) {
    this._map = map;
    return this._container;
  }

  onRemove() {
    this._container.parentNode.removeChild(this._container);
    this.cleanup();
    this._map = undefined;
  }

  open() {
    this._container.style.width = "390px";
    if (!this._showKeyword) {
      this._container.style.width = "225px";
    }
    this._inputContainer.style.display = "inline";
    this.initialize();
  }

  close() {
    this._container.style.width = "28px";
    this._inputContainer.style.display = "none";
    this.cleanup();
  }

  searchKeyword() {
    this._store.dispatch(
      MODULE_PREFIX_SEARCH + GET_SEARCH_KEYWORD,
      this._inputKeyword.value
    );
    let searchValue = this._inputKeyword.value;
    this.handlePageTrackerSiteSearch(
      searchValue,
      SEARCH_CATEGORIES.global_search_keyword,
      0
    );
  }

  initialize() {
    this._autocomplete = new google.maps.places.Autocomplete(
      this._inputLocation,
      {
        types: ["geocode"],
      }
    );

    this._listenerLocation = google.maps.event.addListener(
      this._autocomplete,
      "place_changed",
      () => {
        if (this._autocomplete) {
          this._place = this._autocomplete.getPlace();
          if (this._place) {
            this._store.commit(
              MODULE_PREFIX_SEARCH + SET_SEARCH_LOCATION,
              this._place
            );
            this.addDataToMap();
            this.handlePageTrackerEvent(
              SEARCH_CATEGORIES.global_search_location,
              EVENT_ACTIONS.entered,
              this._place.formatted_address
            );
          }
        }
      }
    );

    this._watcher = this._store.watch(
      (state) => {
        return state.search.location.refreshMap;
      },
      (value) => {
        if (value) {
          if (this._place) {
            this.addDataToMap();
          } else {
            this.removeDataFromMap();
          }
          this._store.dispatch(
            MODULE_PREFIX_SEARCH + GET_SEARCH_LOCATION_REFRESH_MAP,
            false
          );
        }
      }
    );
  }

  addDataToMap() {
    this._features = [];

    if (this._place) {
      if (lodashGet(this._place, "geometry.location")) {
        const LAT = this._place.geometry.location.lat();
        const LNG = this._place.geometry.location.lng();
        const POINT = [LNG, LAT];
        this.updateMap(POINT);
      } else if (this._place.name) {
        const PARTS = this._place.name.split(",");
        if (
          PARTS &&
          Array.isArray(PARTS) &&
          PARTS.length > 1 &&
          !isNaN(PARTS[0]) &&
          !isNaN(PARTS[1])
        ) {
          const LAT = parseFloat(PARTS[0]);
          const LNG = parseFloat(PARTS[1]);
          const POINT = [LNG, LAT];
          this.updateMap(POINT);
        }
      }
    }
  }

  updateMap(point) {
    const GEOMETRY = {
      type: "Point",
      coordinates: point,
    };

    const FEATURE = feature(GEOMETRY);
    if (FEATURE) {
      this._features = [FEATURE];
    }

    const SOURCE = {
      type: "FeatureCollection",
      features: this._features,
    };

    const PARAMS = {
      id: this._layerId,
      noCluster: true,
      iconName: MAP_ICON_NAMES.locations,
    };

    let options = getDefaultMarkerOptions(PARAMS);

    mapMarkerData({
      id: this._layerId,
      data: SOURCE,
      markerOptions: options,
      map: this._map,
      noCluster: true,
    });

    this._map.flyTo({
      zoom: 7,
      center: point,
    });
  }

  removeDataFromMap() {
    removeMapData(this._map, this._layerId);
  }

  clear() {
    this.cleanup();
    this.initialize();
  }

  cleanup() {
    this.removeDataFromMap();
    this._place = null;
    this._autocomplete = null;
    this._inputKeyword.value = "";
    this._inputLocation.value = "";

    google.maps.event.removeListener(this._listenerLocation);

    this._store.commit(MODULE_PREFIX_SEARCH + SET_SEARCH_LOCATION, null);
    this._store.dispatch(MODULE_PREFIX_SEARCH + GET_SEARCH_KEYWORD, null);

    if (this._watcher) {
      this._watcher();
    }
  }

  handlePageTrackerSiteSearch(keyword, category, total) {
    let _paq = (window._paq = window._paq || []);
    _paq.push(["trackSiteSearch", keyword, category, total]);
    pageTrackerLogger(
      "Track Site Search " + keyword + " " + category + " " + total + " logged."
    );
  }

  handlePageTrackerEvent(category, action, name) {
    let _paq = (window._paq = window._paq || []);
    _paq.push(["trackEvent", category, action, name]);
    pageTrackerLogger(
      "Page Tracker Event " + category + " " + action + " " + name + " logged."
    );
  }
}

export default SearchControl;
